/**
 * EVENTSFLO-INBOX-INVOICE-IMPORT-AUDIT-PANEL-V1
 *
 * READ-ONLY admin card listing the most recent
 * `inbox_invoice_import_audit` rows written by Phase 2.
 *
 * Renders ZERO destructive controls — no delete, no rollback,
 * no re-run, no override buttons. Only Refresh + optional
 * invoice-number filter + a row table.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../../lib/api';

const fmtTs = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
};

export default function InboxInvoiceImportAuditCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [exporting, setExporting] = useState(false);

  // EVENTSFLO-ADMIN-AUDIT-PANELS-CSV-EXPORT-V1
  // Safe authed CSV download. Honours the current invoice-number
  // filter and the same 20-row limit shown in the panel. Read-only.
  const downloadCsv = async () => {
    setExporting(true);
    try {
      const f = filter.trim();
      const params = { limit: 20 };
      if (f) params.invoice_number = f;
      const r = await api.get(
        '/admin/inbox/invoice-imports/audit-log/export.csv',
        { params, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let filename = 'inbox_invoice_import_audit.csv';
      const cd = r.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      if (m && m[1]) filename = m[1];
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to download CSV.';
      toast.error(String(msg));
    } finally {
      setExporting(false);
    }
  };

  const load = async (overrideFilter) => {
    setLoading(true);
    try {
      const f = (overrideFilter !== undefined ? overrideFilter : filter).trim();
      const params = { limit: 20 };
      if (f) params.invoice_number = f;
      const r = await api.get(
        '/admin/inbox/invoice-imports/audit-log',
        { params },
      );
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
      setGeneratedAt(r.data?.generated_at || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to load audit log.';
      toast.error(String(msg));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load('');
  }, []);

  return (
    <Card
      id="inbox-invoice-import-audit-panel"
      data-testid="inbox-invoice-import-audit-card"
      className="border-zinc-800"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle data-testid="inbox-invoice-import-audit-title">
            Last 20 Inbox invoice imports
          </CardTitle>
          <p
            className="text-xs text-muted-foreground mt-1"
            data-testid="inbox-invoice-import-audit-readonly-note"
          >
            Read-only import audit history. This panel does not create,
            modify or delete projects.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter by invoice #"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-44 h-8 text-xs"
            data-testid="inbox-invoice-import-audit-filter-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => load()}
            disabled={loading}
            data-testid="inbox-invoice-import-audit-refresh-btn"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadCsv}
            disabled={exporting || loading}
            data-testid="inbox-invoice-import-audit-download-csv-btn"
          >
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {(!rows || rows.length === 0) && !loading && (
          <div
            className="text-xs text-muted-foreground py-4"
            data-testid="inbox-invoice-import-audit-empty"
          >
            No imports recorded yet.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table
              className="w-full text-xs"
              data-testid="inbox-invoice-import-audit-table"
            >
              <thead className="text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1">When</th>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>PO #</th>
                  <th>Shifts</th>
                  <th>Lines</th>
                  <th>Actor</th>
                  <th>Dup ovr</th>
                  <th>TM ovr</th>
                  <th>Warn</th>
                  <th>Import run id</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.import_run_id || i}
                    className="border-t border-zinc-800"
                    data-testid={`inbox-invoice-import-audit-row-${i}`}
                  >
                    <td className="py-1">{fmtTs(r.created_at)}</td>
                    <td className="font-mono">
                      {r.invoice_number || '—'}
                    </td>
                    <td>{r.client_name || r.client_id || '—'}</td>
                    <td>{r.project_name || r.project_id || '—'}</td>
                    <td className="font-mono">{r.po_number || '—'}</td>
                    <td className="text-center">
                      {r.created_shift_count}
                    </td>
                    <td className="text-center">
                      {r.created_line_item_count}
                    </td>
                    <td>{r.actor_email || r.actor_user_id || '—'}</td>
                    <td className="text-center">
                      {r.duplicate_override_used ? (
                        <Badge variant="destructive" className="text-[10px]">
                          yes
                        </Badge>
                      ) : '—'}
                    </td>
                    <td className="text-center">
                      {r.total_mismatch_override_used ? (
                        <Badge variant="destructive" className="text-[10px]">
                          yes
                        </Badge>
                      ) : '—'}
                    </td>
                    <td className="text-center">{r.warnings_count}</td>
                    <td className="font-mono text-[10px] break-all">
                      {r.import_run_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {generatedAt && (
          <div className="text-[10px] text-muted-foreground mt-2 text-right">
            Generated at {fmtTs(generatedAt)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
